<template>
    <Layout disable-vertical-padding disable-horizontal-padding>
        <div v-bind:class="$style.container">
            <VFlex v-bind:class="$style.box">
                <form>
                    <header v-bind:class="$style.header">
                        <h2>Change Password</h2>
                    </header>
                    <VDivider v-bind:class="$style.divider" />
                    <VAlert
                        type="success"
                        outline
                        color="primary"
                        v-bind:value="isPasswordReset"
                        v-bind:class="$style.alert"
                    >
                        Please check your e-mail to create your new password.
                    </VAlert>
                    <VAlert
                        type="error"
                        outline
                        v-bind:value="isInvalidCredentials"
                        v-bind:class="$style.alert"
                    >
                        Your email and password are invalid. Please try again.
                    </VAlert>
                    <VTextField
                        v-model="password"
                        v-bind:type="passwordInputType"
                        label="New Password"
                        v-bind:error-messages="serverErrors.password || TextFieldValidation_getErrorMessages('password')"
                        required
                        v-on:blur="$v.password.$touch()"
                        v-on:keypress.enter="reset"
                    />
                    <VTextField
                        v-model.trim="passwordConfirmation"
                        v-bind:type="passwordInputType"
                        label="New Password Confirmation"
                        v-bind:error-messages="serverErrors.passwordConfirmation || TextFieldValidation_getErrorMessages('passwordConfirmation')"
                        required
                        v-on:blur="$v.passwordConfirmation.$touch()"
                        v-on:keypress.enter="reset"
                    />
                    <VCheckbox
                        v-model="isShowingPassword"
                        label="Show password"
                        hide-details
                    />
                    <VBtn
                        color="primary"
                        depressed
                        large
                        block
                        v-bind:loading="isLoggingIn"
                        v-bind:class="$style.button"
                        v-on:click="reset"
                    >
                        Reset Password
                    </VBtn>
                </form>
            </VFlex>
        </div>
    </Layout>
</template>

<script>
    import Layout from '@layouts/main';
    import TextFieldValidation from '@mixins/TextFieldValidation';
    import {
        required,
    } from 'vuelidate/lib/validators';

    export default {
        /**
         * Custom page meta data property for the view components.
         *
         * @type {Object}
         */
        metaInfo: {
            title: 'Password Reset',
            meta: [
                {
                    name: 'description',
                    content: 'Reset your Citizen J passwrod.',
                },
            ],
            // This is a placeholder to ensure proper canonical is set from specified SEO url
            // Full canonical URL including top level domain should be used
            link: [
                {
                    rel: 'canonical',
                    href: '/change-password',
                },
            ],
        },

        /**
         * Declaratively register required components.
         *
         * @link https://vuejs.org/v2/guide/components.html#Local-Registration
         */
        components: {
            Layout,
        },

        /**
         * Mixins are a flexible way to distribute reusable functionalities for Vue components.
         * A mixin object can contain any component options. When a component uses a mixin,
         * all options in the mixin will be “mixed” into the component’s own options.
         *
         * @link https://vuejs.org/v2/guide/mixins.html
         */
        mixins: [
            TextFieldValidation,
        ],

        /**
         * You must define defaults for all Vue models and reactive Vue template data.
         *
         * @link https://vuejs.org/v2/api/#data
         *
         * @return Object Vue model and binding defaults.
         */
        data () {
            return {
                password: null,
                passwordConfirmation: null,
                serverErrors: {},
                isLoggingIn: false,
                isShowingPassword: false,
                isInvalidCredentials: false,
                isPasswordReset: false,
            };
        },

        /**
         * Cached computed methods for complex logic on the view layer.
         *
         * @link https://vuejs.org/v2/guide/computed.html
         *
         * @type Object The computed functions
         */
        computed: {
            passwordInputType () {
                return this.isShowingPassword ? 'text' : 'password';
            },
        },

        /**
         * This method logic will be run immediately after the Vue instance is created.
         *
         * @link https://vuejs.org/v2/guide/instance.html#Lifecycle-Diagram
         */
        created () {
            // Define the incoming state that redirected to the login view
            this.isPasswordReset = this.$route.query.reset;
        },

        /**
         * Non-cached methods that are used by the Vue instance and template.
         *
         * @type Object ES6 methods
         */
        methods: {
            async reset () {
                // Resets
                this.isInvalidCredentials = false;
                this.serverErrors = {};

                // Verify that the form is valid
                if (this.$v.$invalid) {
                    return this.$v.$touch();
                }

                // Set reactive state defaults
                this.isLoggingIn = true;

                const { passwordConfirmation, password } = this;

                await this.axios
                    .post('/auth/change-password', {
                        password: password,
                        password_confirmation: passwordConfirmation,
                        token: this.$route.params.token,
                    })
                    .then(() => this.$router.push('/login?newPassword=true'))
                    .catch(({ response }) => this.serverErrors = response.data.errors || {})
                    .then(() => this.isSubmitting = false);
            },
        },

        /**
         * Define custom validation rules on reactive data for Vuelidate validation.
         *
         * @link https://monterail.github.io/vuelidate
         *
         * @type {Object}
         */
        validations: {
            password: {
                required,
            },
            passwordConfirmation: {
                required,
            },
        },
    };
</script>

<style lang="scss" module>
    @import '@design';

    .box {
        @apply px-10 pt-12 pb-8 bg-white rounded-lg;
    }

    .header {
        @apply text-center;
    }

    .subheader {
        @apply mt-4;
    }

    .divider {
        @apply my-6;
    }

    .container {
        // This needs to remain nested to overide Vuetify button margins
        .button {
            @apply my-6;
        }
    }

    .bold {
        @apply font-bold;
    }

    .alert {
        @apply mb-4;
    }

    .footer {
        @apply text-center text-xl;
    }

    .forgotPasswordLink {
        @apply text-base;
    }

    @screen sm {
        .container {
            @apply px-6 py-12 flex items-center justify-center;

            // This needs to remain nested to overide Vuetify flex class max-width
            .box {
                max-width: 42rem;
            }
        }
    }
</style>
